<template>
  <div class="bg-gray-100 min-h-screen font-sans" @click="handleOutsideClick">
    <div class="flex flex-wrap items-center gap-3 mb-6 justify-between w-full">
      <h1 class="text-xl font-semibold">Dashboard</h1>
      <!-- filters of app and month and year-->
      <div class="flex items-center gap-1">
        <vs-select v-model="search.app" @change="fetchData" class="w-auto" :color="'#6366f1'">
          <vs-select-item v-for="app in apps" :key="app._id" :value="app.bundleId" :text="app.title" />
        </vs-select>
        <vs-select v-model="search.month" @change="fetchData" class="w-28" :color="'#6366f1'">
          <vs-select-item v-for="month in months" :key="month.value" :value="month.value" :text="month.text" />
        </vs-select>
        <vs-select v-model="search.year" @change="fetchData" class="w-24" :color="'#6366f1'">
          <vs-select-item v-for="year in years" :key="year" :value="year" :text="year" />
        </vs-select>
        <button
          @click="fetchData"
          class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-2 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span>Refresh
        </button>
      </div>
    </div>

    <!-- charts -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      <!-- Active Installs Chart -->
      <div class="bg-white rounded-3xl p-3 shadow">
        <div class="font-bold tracking-wide text-lg">Active Installs</div>
        <div v-if="activeInstallsChartData[0].data.length > 0">
          <apexchart
            type="line"
            :options="activeInstallsChartOptions"
            :series="activeInstallsChartData"
            style="height: 120px; width: 100%"
          ></apexchart>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No data available for the selected filters.</p>
        </div>
      </div>

      <!-- Ratings Chart -->
      <div class="bg-white rounded-3xl p-3 shadow">
        <div class="font-bold tracking-wide text-lg">App Ratings</div>
        <div v-if="ratingsChartData[0].data.length > 0">
          <apexchart type="line" :options="ratingsChartOptions" :series="ratingsChartData" style="height: 120px; width: 100%"></apexchart>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No data available for the selected filters.</p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      <!-- Crashes Chart -->
      <div class="bg-white rounded-3xl p-3 shadow">
        <div class="font-bold tracking-wide text-lg">App Crashes</div>
        <div v-if="crashesChartData[0].data.length > 0">
          <apexchart type="line" :options="crashesChartOptions" :series="crashesChartData" style="height: 120px; width: 100%"></apexchart>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No data available for the selected filters.</p>
        </div>
      </div>

      <!-- Store Performance Chart -->
      <div class="bg-white rounded-3xl p-3 shadow">
        <div class="font-bold tracking-wide text-lg">Store Performance</div>
        <div v-if="storePerformanceChartData[0].data.length > 0">
          <apexchart
            type="line"
            :options="storePerformanceChartOptions"
            :series="storePerformanceChartData"
            style="height: 120px; width: 100%"
          ></apexchart>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No data available for the selected filters.</p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      <!-- daily active charts dummy empty space -->
      <div class="bg-white rounded-3xl p-3 shadow flex flex-col md:col-span-1" style="height: 100%">
        <div class="font-bold tracking-wide text-lg">Daily Active Users</div>
        <div>
          <p class="text-gray-500 text-sm">No data available.</p>
        </div>
      </div>
      <!-- Server Health Section -->
      <div class="bg-white rounded-3xl p-3 shadow">
        <div class="font-bold tracking-wide text-lg flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button :class="activeTab === 'mongo' ? 'font-bold underline text-blue-600' : ''" @click="switchTab('mongo')">MongoDB Status</button>
            <button :class="activeTab === 'reqs' ? 'font-bold underline text-blue-600' : ''" @click="switchTab('reqs')">Slow Requests</button>
          </div>
          <!-- when clicked on button below go to https://${pathname}:3004/status -->
          <button class="text-sm bg-blue-700 text-white rounded-md px-2 py-2" @click="goToServerStatus">Real-time Charts</button>
        </div>
        <div v-if="mongoStatus.length > 0 && activeTab === 'mongo'">
          <apexchart type="line" :options="serverHealthChartOptions" :series="serverHealthChartData" style="height: 300px; width: 100%"></apexchart>
        </div>
        <div v-else-if="slowRequests.length > 0 && activeTab === 'reqs'">
          <div class="overflow-y-auto max-h-96">
            <p class="text-gray-500 text-sm mb-2">Requests taking longer than 2.5s (auto-updates every 5s, upto 25 reqs):</p>
            <table class="w-full table-auto border-collapse">
              <thead>
                <tr class="bg-gray-200">
                  <th class="border px-2 py-1 text-left text-sm">Method</th>
                  <th class="border px-2 py-1 text-left text-sm">URL</th>
                  <th class="border px-2 py-1 text-left text-sm">Response Time (ms)</th>
                  <th class="border px-2 py-1 text-left text-sm">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="request in slowRequests" :key="request.timestamp" class="hover:bg-gray-100">
                  <td class="border px-2 py-1 text-sm">{{ request.method }}</td>
                  <td class="border px-2 py-1 text-sm break-all">{{ request.url }}</td>
                  <td class="border px-2 py-1 text-sm">{{ request.time }}ms</td>
                  <td class="border px- 2 py-1 text-sm">{{ formatResponseDateTime(request.timestamp) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No data available for the selected filters.</p>
        </div>
      </div>
    </div>

    <!-- other -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
      <!-- critical reviews -->
      <div class="bg-white rounded-3xl p-3 shadow flex flex-col md:col-span-1" style="height: 409px">
        <div class="font-bold tracking-wide text-lg">Critical reviews</div>
        <div class="overflow-y-auto pr-1" v-if="reviews.length > 0">
          <div class="flex flex-col gap-1 border-b" v-for="review in reviews" :key="review._id">
            <div class="text-xs font-bold uppercase tracking-wide text-gray-400">
              {{ (apps.length > 0 && apps.find((app) => app.bundleId === review.refAppReview.packageName)).title || "Unknown App" }}
            </div>
            <div class="text-sm font-semibold">{{ review.consumerReview }}</div>
            <div class="flex items-center justify-between text-yellow-400 text-xs mb-1">
              <div>
                <span v-for="n in review.starRating" :key="n" class="material-icons text-base">star</span>
              </div>
              <div class="text-sm text-gray-400" v-if="review.jiraId">Jira ID: {{ review.jiraId }}</div>
            </div>
          </div>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No critical reviews available for selected filters.</p>
        </div>
      </div>
      <!-- notifications -->
      <div class="bg-white rounded-3xl p-3 shadow flex flex-col md:col-span-1" style="height: 409px">
        <div class="flex items-center justify-between">
          <div class="font-bold tracking-wide text-lg">Notifications</div>
          <button
            class="flex items-center gap-1 text-lg border px-2.5 py-0.5 rounded-2xl hover:bg-gray-100 duration-100"
            @click="handleDropdownClick(5)"
          >
            Create
            <span class="material-icons">add</span>
          </button>
        </div>
        <div class="overflow-y-auto pr-1 flex flex-col gap-2" v-if="notifications.length > 0">
          <div class="flex items-center justify-between border-b py-1" v-for="notification in notifications" :key="notification._id">
            <!-- circular outlined notification bell icon -->
            <div class="flex items-center gap-2">
              <span class="material-icons text-gray-500 border rounded-full p-1">notifications_none</span>
              <div class="flex flex-col gap-1">
                <div class="text-sm font-semibold">{{ notification.title }}</div>
                <div class="text-xs text-gray-400">{{ formatDate(notification.customDate) }} / {{ notification.customTime }}</div>
              </div>
            </div>
            <button
              class="text-gray-500 hover:text-gray-700 border rounded-full p-1 flex items-center justify-center hover:bg-gray-100 duration-100"
              @click="redirectToNotification(notification._id)"
              title="Edit Notification"
            >
              <span class="material-icons">edit</span>
            </button>
          </div>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No notifications available for selected filters.</p>
        </div>
      </div>
      <!-- events -->
      <div class="bg-white rounded-3xl p-3 shadow flex flex-col md:col-span-1" style="height: 409px">
        <div class="flex items-center justify-between relative">
          <div class="font-bold tracking-wide text-lg">Events</div>
          <button
            class="flex items-center gap-1 text-lg border px-2.5 py-0 rounded-2xl hover:bg-gray-100 duration-100"
            @click.stop="showCreateOptionsDropdown = !showCreateOptionsDropdown"
          >
            Create
            <span class="material-icons transform duration-200" :class="{ 'rotate-45': showCreateOptionsDropdown }"> add </span>
          </button>
          <!-- dropdown -->
          <div
            v-if="showCreateOptionsDropdown"
            ref="createOptionsDropdown"
            class="absolute top-9 right-0 bg-white border rounded-2xl shadow-lg z-10 w-auto overflow-hidden"
          >
            <ul class="divide-y">
              <li class="px-3 py-1.5 text-base hover:bg-gray-100 cursor-pointer" @click="handleDropdownClick(0)">Create New Event</li>
              <li class="px-3 py-1.5 text-base hover:bg-gray-100 cursor-pointer" @click="handleDropdownClick(1)">Create New Article</li>
              <li class="px-3 py-1.5 text-base hover:bg-gray-100 cursor-pointer" @click="handleDropdownClick(2)">Create New Music</li>
              <li class="px-3 py-1.5 text-base hover:bg-gray-100 cursor-pointer" @click="handleDropdownClick(3)">Create New Post</li>
              <li class="px-3 py-1.5 text-base hover:bg-gray-100 cursor-pointer" @click="handleDropdownClick(4)">Create New Festival</li>
            </ul>
          </div>
        </div>
        <div class="overflow-y-auto pr-1 flex flex-col gap-2" v-if="events.length > 0">
          <div class="flex items-center justify-between border-b py-1" v-for="event in events" :key="event._id">
            <div class="flex flex-col gap-1">
              <div class="text-sm font-semibold">{{ event.title }}</div>
              <div class="text-xs text-gray-400">{{ formatDate(event.startDate) }} / From {{ event.startTime }}</div>
            </div>
            <button
              class="text-gray-500 hover:text-gray-700 border rounded-full p-1 flex items-center justify-center hover:bg-gray-100 duration-100"
              @click="redirectToEvents(event._id)"
              title="Edit Event"
            >
              <span class="material-icons">edit</span>
            </button>
          </div>
        </div>
        <div v-else>
          <p class="text-gray-500 text-sm mt-2">No events available for selected filters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/rest.js";
import axios from "axios";
import { API } from "@/common/constants";
import moment from "moment";
import VueApexCharts from "vue-apexcharts";
import { defaultToolbarOptions } from "@/util/chartToolbarOptions";

export default {
  name: "DashboardRedesign",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      activeTab: "mongo",
      apps: [],
      reviews: [],
      notifications: [],
      events: [],
      dailyActiveUsers: [],
      months: [
        { value: 1, text: "January" },
        { value: 2, text: "February" },
        { value: 3, text: "March" },
        { value: 4, text: "April" },
        { value: 5, text: "May" },
        { value: 6, text: "June" },
        { value: 7, text: "July" },
        { value: 8, text: "August" },
        { value: 9, text: "September" },
        { value: 10, text: "October" },
        { value: 11, text: "November" },
        { value: 12, text: "December" },
      ],
      years: [],
      search: {
        app: "com.india.hindicalender",
        month: moment().month() + 1,
        year: moment().year(),
      },
      showCreateOptionsDropdown: false,

      activeInstallsChartOptions: {
        chart: {
          id: "active-installs",
        },
        toolbar: defaultToolbarOptions,
        xaxis: {
          categories: [],
          title: {
            text: "Date",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
        yaxis: {
          title: {
            text: "Active Installs",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
      },
      activeInstallsChartData: [
        {
          name: "Active Installs",
          data: [],
        },
        {
          name: "New Installs",
          data: [],
        },
      ],

      ratingsChartOptions: {
        chart: {
          id: "ratings",
        },
        toolbar: defaultToolbarOptions,
        xaxis: {
          categories: [],
          title: {
            text: "Date",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
        yaxis: {
          title: {
            text: "Ratings",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
      },
      ratingsChartData: [
        {
          name: "Daily Avg Rating",
          data: [],
        },
        {
          name: "Total Avg Rating",
          data: [],
        },
      ],

      crashesChartOptions: {
        chart: {
          id: "crashes",
        },
        toolbar: defaultToolbarOptions,
        xaxis: {
          categories: [],
          title: {
            text: "Date",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
        yaxis: {
          title: {
            text: "Crashes / ANRs",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        },
      },
      crashesChartData: [
        {
          name: "Daily Crashes",
          data: [],
        },
        {
          name: "Daily ANRs",
          data: [],
        },
      ],

      storePerformanceChartOptions: {
        chart: {
          id: "store-performance",
          type: "line",
        },
        toolbar: defaultToolbarOptions,
        xaxis: {
          categories: [],
          title: {
            text: "Date",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
          labels: {
            rotate: -45,
            style: {
              fontSize: "10px",
            },
          },
        },
        yaxis: {
          title: {
            text: "Values",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
          labels: {
            formatter: (value) => value.toFixed(0),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const visitors = series[0][dataPointIndex];
            const acquisitions = series[1][dataPointIndex];
            const conversionRate = (acquisitions / visitors) * 100;
            return `<div style='padding: 10px;'>
                        <strong>Visitors:</strong> ${visitors}<br>
                        <strong>Acquisitions:</strong> ${acquisitions}<br>
                        <strong>Conversion Rate:</strong> ${conversionRate.toFixed(0)}%
                      </div>`;
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
      storePerformanceChartData: [
        {
          name: "Visitors",
          data: [],
        },
        {
          name: "Acquisitions",
          data: [],
        },
      ],
      mongoStatus: [],
      slowRequests: [],
      slowReqInterval: null,
      serverHealthChartOptions: {
        chart: {
          id: "server-health",
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: "Server Metrics",
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            show: false,
          },
        },
        grid: {
          show: false,
        },
        stroke: {
          curve: "smooth",
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              if (seriesIndex === 0) {
                const days = Math.floor(value / 86400);
                const hours = Math.floor((value % 86400) / 3600);
                const minutes = Math.floor((value % 3600) / 60);
                const seconds = value % 60;
                return `${days}d ${hours}h ${minutes}m ${seconds}s`;
              } else if (seriesIndex === 1) {
                return `${value} connections`;
              } else if (seriesIndex === 2) {
                return `${(value / 1024).toFixed(2)} MB`;
              }
              return value;
            },
          },
        },
      },
      serverHealthChartData: [
        { name: "Uptime (s)", data: [] },
        { name: "Connections (current)", data: [] },
        { name: "Memory (virtual)", data: [] },
      ],
    };
  },
  methods: {
    formatDate(date) {
      return moment(date).format("DD MMM YYYY");
    },

    formatResponseDateTime(dateTime) {
      return moment(dateTime).format("DD/MM/YY HH:mm A");
    },

    switchTab(tab) {
      this.activeTab = tab;
    },

    goToServerStatus() {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const url = `${protocol}//${hostname}:3004/status`;
      window.open(url, "_blank");
    },

    handleDropdownClick(idx) {
      const addNew = "true";
      this.showCreateOptionsDropdown = false;

      switch (idx) {
        case 0:
          this.$router.push({ path: "/events", query: { addNew } });
          break;
        case 1:
          this.$router.push({ path: "/articlecategory", query: { addNew } });
          break;
        case 2:
          this.$router.push({ path: "/musicCategory", query: { addNew } });
          break;
        case 3:
          this.$router.push({ path: "/daily-status", query: { addNew } });
          break;
        case 4:
          this.$router.push({ path: "/festival", query: { addNew } });
          break;
        case 5:
          this.$router.push({ path: "/notificationSrv", query: { addNew } });
          break;
        default:
          break;
      }
    },

    handleOutsideClick(event) {
      const dropdown = this.$refs.createOptionsDropdown;
      if (this.showCreateOptionsDropdown && dropdown && !dropdown.contains(event.target)) {
        this.showCreateOptionsDropdown = false;
      }
    },

    redirectToEvents(id) {
      const editFromDashboard = true;
      this.$router.push({ path: "/events", query: { id, editFromDashboard } });
    },

    redirectToNotification(id) {
      this.$router.push({ path: "/notificationSrv", query: { id, language: this.search.language } });
    },

    async fetchData() {
      this.$vs.loading();
      try {
        const response = await api.get(`${API.DASHBOARD_METRICS}?packageName=${this.search.app}&year=${this.search.year}&month=${this.search.month}`);
        if (response.status !== 200) throw new Error("Failed to fetch dashboard metrics");

        const data = response.data.fetchGooglePlayAppMetrics;

        if (this.search.month !== parseInt(data.month.split("-")[1])) {
          const yearText = this.months.find((m) => m.value === this.search.month).text;

          this.$vs.notify({
            title: "Notice",
            text: `${yearText}-${this.search.year} data is not available. Showing data for the most recent available month.`,
            color: "warning",
            position: "top-right",
          });

          this.search.month = parseInt(data.month.split("-")[1]);
          this.search.year = parseInt(data.month.split("-")[0]);
        }

        this.notifications = response.data.notifications || [];
        this.events = response.data.events || [];
        this.reviews = response.data.criticalReviews || [];
        this.mongoStatus = response.data.mongoStatus || [];

        const days = this.mongoStatus.map((d) => d.day);
        const uptimes = this.mongoStatus.map((d) => d.uptimeDelta);
        const connections = this.mongoStatus.map((d) => d.connections);
        const memory = this.mongoStatus.map((d) => d.memory);

        this.serverHealthChartOptions = {
          ...this.serverHealthChartOptions,
          xaxis: {
            categories: days,
          },
        };

        this.serverHealthChartData = [
          { name: "Uptime (s)", data: uptimes },
          { name: "Connections (current)", data: connections },
          { name: "Memory (virtual)", data: memory },
        ];

        const dates = data.crashes.daily.map((item) => moment(item.date).format("DD MMM"));
        this.activeInstallsChartOptions = {
          ...this.activeInstallsChartOptions,
          xaxis: {
            ...this.activeInstallsChartOptions.xaxis,
            categories: dates,
          },
        };
        this.ratingsChartOptions = {
          ...this.ratingsChartOptions,
          xaxis: {
            ...this.ratingsChartOptions.xaxis,
            categories: dates,
          },
        };
        this.crashesChartOptions = {
          ...this.crashesChartOptions,
          xaxis: {
            ...this.crashesChartOptions.xaxis,
            categories: dates,
          },
        };
        this.storePerformanceChartOptions = {
          ...this.storePerformanceChartOptions,
          xaxis: {
            ...this.storePerformanceChartOptions.xaxis,
            categories: dates,
          },
        };

        this.activeInstallsChartData = [
          {
            name: "Active Installs",
            data: data.installs.daily.map((item) => item.activeInstalls),
          },
          {
            name: "New Installs",
            data: data.installs.daily.map((item) => item.deviceInstalls),
          },
        ];

        this.ratingsChartData = [
          {
            name: "Daily Avg Rating",
            data: data.ratings.daily.map((item) => item.dailyAvgRating),
          },
          {
            name: "Total Avg Rating",
            data: data.ratings.daily.map((item) => item.totalAvgRating),
          },
        ];

        this.crashesChartData = [
          {
            name: "Daily Crashes",
            data: data.crashes.daily.map((item) => item.dailyCrashes),
          },
          {
            name: "Daily ANRs",
            data: data.crashes.daily.map((item) => item.dailyAnrs),
          },
        ];

        this.storePerformanceChartData = [
          {
            name: "Visitors",
            data: data.storePerformance.daily.filter((item) => item.country === "IN").map((item) => parseFloat(item.visitors.toFixed(2))),
          },
          {
            name: "Acquisitions",
            data: data.storePerformance.daily.filter((item) => item.country === "IN").map((item) => parseFloat(item.acquisitions.toFixed(2))),
          },
        ];
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        this.$vs.notify({
          title: "Error",
          text: "Failed to fetch dashboard data.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchServerHealth() {
      let pathname = window.location.origin.split("//")[1];
      let baseUrl = pathname.includes("localhost") ? "http://localhost:3004" : `https://${pathname}:3004`;
      try {
        const r = await axios.get(`${baseUrl}/slow-requests`, {
          headers: {
            api_key: "anvl_bharat_cal123",
          },
        });
        if (r.status === 200) this.$data.slowRequests = r.data;
      } catch (error) {
        console.error("Error fetching server health data:", error);
      }
    },

    async fetchAllApps() {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.GET_APP}/plain?language=all`);
        if (r.status === 200) this.$data.apps = r.data;
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Failed to fetch apps.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    generateYears() {
      const currentYear = moment().year();
      for (let year = 2021; year <= currentYear; year++) {
        this.years.push(year);
      }
    },
  },
  async mounted() {
    this.generateYears();
    await Promise.all([this.fetchData(), this.fetchAllApps(), this.fetchServerHealth()]);
    this.intervalId = setInterval(() => {
      this.fetchServerHealth();
    }, 5000);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/icon?family=Material+Icons");

.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
